import type { ChangeEvent } from "react";
import { useCallback, useRef, useState } from "react";
import { useAppDispatch } from "../../../services/redux/hooks/use-dispatch";
import { useAppSelector } from "../../../services/redux/hooks/use-selector";
import { startDialogAction, uploadFileToDialogAction } from "../redux/actions";
import {
  selectDialogMessages,
  selectDialogValue,
  selectIsFileLoading,
} from "../redux/selectors";
import { addUserMessage } from "../redux/slice";
import { hiddenSecurityPart } from "../../../shared/utils/hiddenSecurityPart";
import { getFileExtension } from "../../Meeting/helpers/getFileExtension";
import { getArtifactContentAction } from "../../Files/redux/actions";
import { getFileConfig } from "../../Files/helpers";
import { UPLOADED_FILE_EXTENSIONS } from "../../Modals/AttachedFileModal/constants";
import { FILE_CHAT_META_INSTRUCTION } from "../../../common/Chat/constants";
import { createNotification } from "../../../app/state/notificationSlice/slice";

export const useDialogs = () => {
  const dispatch = useAppDispatch();
  const value = useAppSelector(selectDialogValue);
  const messages = useAppSelector(selectDialogMessages);
  const isFileLoading = useAppSelector(selectIsFileLoading);
  const [message, setMessage] = useState<string>("");
  const [fileName, setFileName] = useState<string | null>(null);
  const [fileContent, setFileContent] = useState<string>("");
  const inputRef = useRef<HTMLInputElement | null>(null);
  const uploadAbortFnRef = useRef<(() => void) | null>(null);

  const handleChangeMessage = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(event.target.value);
  };

  const handleFileUpload = (e: any) => {
    if (!e.target.files[0]) return;
    const file = e.target.files[0];
    setFileName(file.name);

    const fileExtension = getFileExtension(file.name) ?? "";
    if (!UPLOADED_FILE_EXTENSIONS.includes(fileExtension)) {
      dispatch(
        createNotification(
          "warning",
          `Расширение загруженного файла (${fileExtension}) не поддерживается`,
        ),
      );
      setFileName(null);
      setFileContent("");
      if (inputRef.current) {
        inputRef.current.value = "";
      }
      return;
    }

    const promise = dispatch(uploadFileToDialogAction(file));
    const abortFn = () => promise.abort();
    uploadAbortFnRef.current = abortFn;

    promise
      .unwrap()
      .then((text) => {
        setFileContent(text);
      })
      .catch((error) => {
        if (error !== "Aborted") {
          console.error(error);
        }
      });
  };

  const handleClick = useCallback(
    async (hash: string, fileName: string) => {
      const fileConfig = getFileConfig(fileName);
      if (!fileConfig) return;
      setFileName(fileName);

      const fileExtension = getFileExtension(fileName) ?? "";
      if (!UPLOADED_FILE_EXTENSIONS.includes(fileExtension)) {
        dispatch(
          createNotification(
            "warning",
            `Расширение загруженного файла (${fileExtension}) не поддерживается`,
          ),
        );
        setFileName(null);
        setFileContent("");
        if (inputRef.current) {
          inputRef.current.value = "";
        }
        return;
      }

      const promise = dispatch(getArtifactContentAction({ hash, fileName }));
      const abortFn = () => promise.abort();
      uploadAbortFnRef.current = abortFn;

      promise
        .unwrap()
        .then((content) => {
          setFileContent(content);
        })
        .catch((error) => {
          if (error !== "Aborted") {
            console.error(error);
          }
        });
    },
    [fileName, fileContent],
  );

  const handleSendMessage = useCallback(() => {
    if (message.trim() === "") return;
    if (!value?.dialog_object?.assistant_ref && !value?.dialog_object?.model) {
      dispatch(createNotification("warning", "Выберите модель или ассистента"));
      return;
    }
    const finalMessage = fileName
      ? `${message} <security> ${FILE_CHAT_META_INSTRUCTION} ${fileContent}</security>  \n\n<file>${fileName}</file>`
      : message;
    dispatch(addUserMessage(hiddenSecurityPart(finalMessage)));
    dispatch(startDialogAction(finalMessage));
    setMessage("");
    setFileName(null);
    setFileContent("");
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  }, [
    message,
    value?.dialog_object?.assistant_ref,
    value?.dialog_object?.model,
    fileName,
    fileContent,
    dispatch,
  ]);

  const handleSendKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.ctrlKey && event.key === "Enter") {
        event.preventDefault();
        handleSendMessage();
      }
    },
    [
      message,
      value?.dialog_object?.assistant_ref,
      value?.dialog_object?.model,
      fileName,
      fileContent,
      dispatch,
    ],
  );

  const handleDeleteFileTag = () => {
    uploadAbortFnRef.current?.();
    setFileName(null);
    setFileContent("");
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  return {
    messages,
    message,
    fileName,
    isFileLoading,
    inputRef,
    handleClick,
    handleDeleteFileTag,
    handleFileUpload,
    handleChangeMessage,
    handleSendKeyDown,
    handleSendMessage,
  };
};
